import Sheet from '@mui/joy/Sheet';
import IconButton from '@mui/joy/IconButton';
import GlobalStyles from '@mui/joy/GlobalStyles';
import { toggleSidebar, openSidebar } from '../../utils';
import { SwapHorizontalCircleOutlined, MenuRounded, SupportAgent } from "@mui/icons-material";
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import React, { useEffect, useState } from 'react';
import { Link, Select, Tooltip } from '@mui/joy';
import Option from '@mui/joy/Option';
import Badge from '@mui/joy/Badge';
import Button from '@mui/joy/Button';
import { useUser } from "./UserContext";
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import ApiService, { GetTicketStats } from "../../axios/Axios";
import { ActiveCallIndicator } from "../ActiveCallIndicator";
import { isStripeTestMode } from "../../stripeConfig";
import Chip from "@mui/joy/Chip";

// React Toastify Imports
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

// Mappings to translate between UI and API
const UI_TO_API = {
    'Online': 'AVAILABLE',
    'Offline': 'OFFLINE'
};

const API_TO_UI = {
    'AVAILABLE': 'Online',
    'ON_CALL': 'Online',
    'OFFLINE': 'Offline'
};

export default function Header() {
    const { user } = useUser();
    const navigate = useNavigate();
    const [salesStatus, setSalesStatus] = useState('Offline');
    const [callRouting, setCallRouting] = useState('CRM');
    const [ticketUnreadCount, setTicketUnreadCount] = useState(0);
    const hasSalesPermission = user?.location_permissions?.includes("sales") ||
        user?.location_permissions?.includes("sales_admin");
    const hasAdminPermission = user?.location_permissions?.includes("admin");
    const isSuperAdmin = user?.location_permissions?.includes("super_admin");
    const canSeeTickets = hasAdminPermission || isSuperAdmin;

    useEffect(() => {
        if (hasSalesPermission) {
            ApiService.getSalesStatus()
                .then((data) => {
                    const uiStatus = API_TO_UI[data.sales_status] || 'Offline';
                    setSalesStatus(uiStatus);
                    setCallRouting(data.call_routing || 'CRM');
                })
                .catch((err) => console.error("Error fetching sales status:", err));
        }
    }, [hasSalesPermission]);

    useEffect(() => {
        if (!canSeeTickets) {
            setTicketUnreadCount(0);
            return;
        }

        let mounted = true;
        let timerId: ReturnType<typeof setInterval> | null = null;

        const loadTicketStats = async () => {
            try {
                const res = await GetTicketStats();
                if (!mounted) return;
                setTicketUnreadCount(Number(res.data?.unread_count || 0));
            } catch (_error) {
                if (!mounted) return;
                setTicketUnreadCount(0);
            }
        };

        loadTicketStats();
        timerId = setInterval(loadTicketStats, 60000);

        return () => {
            mounted = false;
            if (timerId) clearInterval(timerId);
        };
    }, [canSeeTickets]);

    // 2. Handle Dropdown Change with Toast Notification
    const handleStatusChange = (newValue) => {
        if (!newValue) return;

        // Optimistically update UI
        setSalesStatus(newValue);

        // Determine backend value
        const backendValue = UI_TO_API[newValue];

        // Call API
        ApiService.updateSalesStatus(backendValue)
            .then(() => {
                // Success Toast
                if (newValue === 'Online') {
                    toast.info("Status updated: You are now Online", {
                        icon: "🟢"
                    });
                } else {
                    toast.info("Status updated: You are now Offline", {
                        icon: "⚫"
                    });
                }
            })
            .catch((err) => {
                console.error("Error updating sales status:", err);
                toast.error("Failed to update status. Please try again.");
                // Optional: Revert UI if API fails
            });
    };

    const handleCallRoutingChange = (newValue) => {
        if (!newValue || newValue === callRouting) return;
        const previous = callRouting;
        setCallRouting(newValue);

        ApiService.updateCallRouting(newValue)
            .then(() => {
                toast.info(newValue === 'PHONE'
                    ? "Calls will ring your phone. Press 1 to accept."
                    : "Calls will ring here in the CRM.");
            })
            .catch((err) => {
                console.error("Error updating call routing:", err);
                setCallRouting(previous);
                const message = err?.response?.data?.call_routing?.[0];
                toast.error(message || "Failed to change where calls ring. Please try again.");
            });
    };

    return (
        <>
    <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        style={{ zIndex: 10001 }} // Ensure it sits on top of the Header and Select
    />

        <Sheet
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                position: 'fixed',
                top: 0,
                // On mobile, header takes full width; on larger screens, adjusts for sidebar
                left: {
                    xs: 0,
                    sm: 'calc(var(--SideNavigation-slideIn, 0) * var(--Sidebar-width))',
                },
                width: {
                    xs: '100%',
                    sm: 'calc(100% - (var(--SideNavigation-slideIn, 0) * var(--Sidebar-width)))',
                },
                height: 'var(--Header-height)',
                zIndex: 9995,
                px: { xs: 1.5, sm: 2 },
                py: 1,
                gap: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                transition: 'left 0.4s, width 0.4s',
            }}
        >
            <GlobalStyles
                styles={{
                    ':root': {
                        '--Header-height': '72px',
                    },
                }}
            />

            {/* Left side of the header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Hamburger menu for mobile */}
                <IconButton
                    onClick={() => openSidebar()}
                    variant="outlined"
                    color="neutral"
                    size="sm"
                    sx={{ display: { xs: 'flex', sm: 'none' } }}
                >
                    <MenuRounded />
                </IconButton>
                {/* Toggle sidebar button for tablet/desktop */}
                <IconButton
                    onClick={() => toggleSidebar()}
                    variant="outlined"
                    color="neutral"
                    size="sm"
                    sx={{ display: { xs: 'none', sm: 'flex' } }}
                >
                    <SwapHorizontalCircleOutlined />
                </IconButton>

                {canSeeTickets && (
                    <Badge
                        badgeContent={ticketUnreadCount}
                        color="danger"
                        invisible={ticketUnreadCount === 0}
                        size="sm"
                    >
                        <Button
                            size="sm"
                            variant="soft"
                            color={ticketUnreadCount > 0 ? 'primary' : 'neutral'}
                            startDecorator={<SupportAgent />}
                            onClick={() => navigate('/dashboard/support-tickets')}
                            sx={{
                                fontWeight: 600,
                                display: { xs: 'none', sm: 'flex' },
                            }}
                        >
                            Support & Tickets
                        </Button>
                        <IconButton
                            size="sm"
                            variant="soft"
                            color={ticketUnreadCount > 0 ? 'primary' : 'neutral'}
                            onClick={() => navigate('/dashboard/support-tickets')}
                            sx={{
                                display: { xs: 'flex', sm: 'none' },
                            }}
                        >
                            <SupportAgent />
                        </IconButton>
                    </Badge>
                )}
            </Box>

            {/* Right side of the header (always visible) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                {isStripeTestMode() && (
                    <Chip size="sm" color="warning" variant="soft">
                        Stripe test mode
                    </Chip>
                )}
                {hasSalesPermission && (
                    <>
                        {/* Hide label on mobile */}
                        <Typography
                            level="title-sm"
                            sx={{ 
                                color: 'text.secondary',
                                display: { xs: 'none', md: 'block' }
                            }}
                        >
                            Sales Status:
                        </Typography>

                        <Box sx={{ position: 'relative' }}>
                            <Select
                                value={salesStatus}
                                onChange={(event, newValue) => handleStatusChange(newValue)}
                                size="sm"
                                variant="soft"
                                color={salesStatus === 'Online' ? 'success' : 'neutral'}

                                // Fix for Z-Index issue
                                slotProps={{
                                    listbox: {
                                        sx: { zIndex: 10000 }
                                    }
                                }}

                                startDecorator={
                                    <StatusDot color={salesStatus === 'Online' ? 'success.500' : 'neutral.400'} />
                                }
                                sx={{
                                    minWidth: { xs: 100, sm: 140 },
                                    fontWeight: 600,
                                    borderRadius: 'md',
                                    transition: '0.2s',
                                    '&:hover': {
                                        zIndex: 100,
                                        bgcolor: salesStatus === 'Online' ? 'success.softHover' : 'neutral.softHover',
                                    }
                                }}
                            >
                                <Option value="Online" label="Online">
                                    <StatusDot color="success.500" />
                                    <Typography sx={{ ml: 1 }}>Online</Typography>
                                </Option>
                                <Option value="Offline" label="Offline">
                                    <StatusDot color="neutral.400" />
                                    <Typography sx={{ ml: 1 }}>Offline</Typography>
                                </Option>
                            </Select>

                            {/* Hide schedule link on mobile */}
                            <Typography
                                level="body-xs"
                                sx={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    width: '100%',
                                    mt: 0.5,
                                    lineHeight: 1,
                                    display: { xs: 'none', sm: 'block' }
                                }}
                            >
                                <Link
                                    component={RouterLink}
                                    to="/dashboard/mysales"
                                    underline="hover"
                                    sx={{
                                        color: 'text.tertiary',
                                        fontSize: '11px',
                                        '&:hover': { color: 'primary.plainColor' }
                                    }}
                                >
                                    Click to manage schedule
                                </Link>
                            </Typography>
                        </Box>

                        <Select
                            value={callRouting}
                            onChange={(event, newValue) => handleCallRoutingChange(newValue)}
                            size="sm"
                            variant="soft"
                            slotProps={{
                                listbox: {
                                    sx: { zIndex: 10000 }
                                }
                            }}
                            sx={{ minWidth: { xs: 90, sm: 130 }, fontWeight: 600, borderRadius: 'md' }}
                        >
                            <Option value="CRM" label="Calls: CRM">Ring in the CRM</Option>
                            <Option value="PHONE" label="Calls: Phone">Ring my phone</Option>
                        </Select>
                    </>
                )}

                {/* Divider between sales status and VoIP indicator (only for sales users) */}
                {hasSalesPermission && (
                    <Box
                        sx={{
                            width: '1px',
                            height: 28,
                            bgcolor: 'divider',
                            mx: { xs: 0.5, sm: 1 },
                        }}
                    />
                )}

                {/* VoIP indicator visible to everyone */}
                <ActiveCallIndicator />
            </Box>


        </Sheet>
        </>

    );
}

const StatusDot = ({ color }) => (
    <Box
        sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: color,
            boxShadow: color === 'success.500' ? '0 0 8px rgba(33, 196, 93, 0.5)' : 'none',
        }}
    />
);